// ManiDine Restaurant Web — src/pages/reports/OrdersReports.jsx — Updated: 2026-03-31 16:39 (America/Edmonton)
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import useRestaurantContext from "../../hooks/useRestaurantContext";
import {
  ReportCard,
  ReportChart,
  ReportFilterBar,
  ReportTable,
} from "../../components/reports";

const defaultDateRange = { preset: "7d", start: "", end: "" };

// Helpers
const getOrderDate = (createdAt) => {
  if (!createdAt) return null;
  if (typeof createdAt.toDate === "function") return createdAt.toDate();
  if (createdAt instanceof Date) return createdAt;
  const d = new Date(createdAt);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getOrderTimelineDate = (order) =>
  getOrderDate(
    order?.createdAt || order?.placedAt || order?.orderTime || order?.updatedAt
  );

const parseNumber = (value, fallback = 0) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(parseNumber(value));

const normalizeStatus = (status) => {
  if (!status) return "";
  return String(status).toLowerCase();
};

const isSalesEligibleStatus = (status) => {
  const s = normalizeStatus(status);
  if (!s) return true;
  return !(
    s.includes("cancel") ||
    s.includes("reject") ||
    s.includes("refun") ||
    s.includes("void") ||
    s.includes("declin")
  );
};

const isCompletedStatus = (status) => {
  const s = normalizeStatus(status);
  if (!s) return false;
  if (!isSalesEligibleStatus(s)) return false;
  return (
    s.includes("complete") ||
    s.includes("deliver") ||
    s.includes("finish") ||
    s.includes("paid")
  );
};

const isCancelledStatus = (status) => {
  const s = normalizeStatus(status);
  if (!s) return false;
  return (
    s.includes("cancel") ||
    s.includes("reject") ||
    s.includes("declin") ||
    s.includes("void")
  );
};

const isRefundedStatus = (status) => normalizeStatus(status).includes("refun");

const resolveDateRange = (dateRange) => {
  const now = new Date();
  let start = null;
  let end = null;

  if (dateRange.preset === "today") {
    start = new Date();
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 1);
  } else if (dateRange.preset === "7d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "30d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "custom") {
    if (dateRange.start) {
      start = new Date(dateRange.start);
      start.setHours(0, 0, 0, 0);
    }
    if (dateRange.end) {
      end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
    }
  }

  return { start, end };
};

const getOrderTax = (order) => {
  if (typeof order?.tax === "number") return order.tax;
  if (typeof order?.gst === "number") return order.gst;
  if (typeof order?.taxAmount === "number") return order.taxAmount;
  return 0;
};

const getFoodSubtotal = (order) => {
  if (typeof order?.subtotal === "number") return order.subtotal;
  if (typeof order?.itemsSubtotal === "number") return order.itemsSubtotal;
  if (typeof order?.foodSubtotal === "number") return order.foodSubtotal;

  const total =
    typeof order?.totalAmount === "number"
      ? order.totalAmount
      : typeof order?.total === "number"
      ? order.total
      : 0;

  if (!total) return 0;

  const tax = getOrderTax(order);
  const deliveryFee = parseNumber(order?.deliveryFee);
  const tip =
    typeof order?.tip === "number"
      ? order.tip
      : typeof order?.tipAmount === "number"
      ? order.tipAmount
      : 0;

  return Math.max(total - tax - deliveryFee - tip, 0);
};

const getRestaurantSalesTotal = (order, salesEligible) => {
  if (!salesEligible) return 0;
  return getFoodSubtotal(order) + getOrderTax(order);
};

const OrdersReports = () => {
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load all orders for restaurant
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        if (!restaurantId) {
          setOrders([]);
          setLoading(false);
          return;
        }
        const ordersRef = collection(db, "orders");
        const ordersQuery = query(
          ordersRef,
          where("restaurantId", "==", restaurantId)
        );
        const snap = await getDocs(ordersQuery);
        const rows = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        console.log("[OrdersReports] loaded:", rows.length);
        setOrders(rows);
      } catch (err) {
        console.error("[OrdersReports] Load failed:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantLoading) {
      setLoading(true);
      return;
    }

    fetchOrders();
  }, [restaurantId, restaurantLoading]);

  // Apply date filter and derive restaurant-only sales values
  const filteredOrders = useMemo(() => {
    const { start, end } = resolveDateRange(dateRange);
    return orders
      .filter((order) => {
        const d = getOrderTimelineDate(order);
        if (!d) return false;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      })
      .map((order) => {
        const status = order.status || "";
        const salesEligible = isSalesEligibleStatus(status);
        const foodSubtotal = getFoodSubtotal(order);
        const tax = getOrderTax(order);
        const restaurantSalesTotal = getRestaurantSalesTotal(order, salesEligible);

        return {
          ...order,
          __status: status,
          __salesEligible: salesEligible,
          __foodSubtotal: foodSubtotal,
          __tax: tax,
          __restaurantSalesTotal: restaurantSalesTotal,
        };
      });
  }, [orders, dateRange]);

  // KPIs
  const kpis = useMemo(() => {
    const total = filteredOrders.length;
    const completed = filteredOrders.filter((o) => isCompletedStatus(o.__status)).length;
    const cancelled = filteredOrders.filter((o) => isCancelledStatus(o.__status)).length;
    const refunded = filteredOrders.filter((o) => isRefundedStatus(o.__status)).length;

    const avgPrep =
      filteredOrders.reduce((s, o) => s + parseNumber(o.prepTimeMinutes), 0) /
      (filteredOrders.length || 1);
    const avgDelivery =
      filteredOrders.reduce((s, o) => s + parseNumber(o.deliveryTimeMinutes), 0) /
      (filteredOrders.length || 1);

    const restaurantFoodSales = filteredOrders.reduce(
      (sum, o) => sum + (o.__salesEligible ? o.__foodSubtotal : 0),
      0
    );
    const restaurantTax = filteredOrders.reduce(
      (sum, o) => sum + (o.__salesEligible ? o.__tax : 0),
      0
    );
    const restaurantSalesTotal = filteredOrders.reduce(
      (sum, o) => sum + o.__restaurantSalesTotal,
      0
    );

    return {
      total,
      completed,
      cancelled,
      refunded,
      avgPrep: Number.isFinite(avgPrep) ? avgPrep : 0,
      avgDelivery: Number.isFinite(avgDelivery) ? avgDelivery : 0,
      restaurantFoodSales,
      restaurantTax,
      restaurantSalesTotal,
    };
  }, [filteredOrders]);

  // Prep time over time
  const prepTimeSeries = useMemo(() => {
    const map = new Map();
    filteredOrders.forEach((o) => {
      const d = getOrderTimelineDate(o);
      if (!d) return;
      const key = d.toLocaleDateString();
      const prev = map.get(key) || { count: 0, total: 0 };
      map.set(key, {
        count: prev.count + 1,
        total: prev.total + parseNumber(o.prepTimeMinutes),
      });
    });
    return [...map.entries()].map(([date, v]) => ({
      date,
      avgPrep: v.count ? v.total / v.count : 0,
    }));
  }, [filteredOrders]);

  // Delivery time over time
  const deliveryTimeSeries = useMemo(() => {
    const map = new Map();
    filteredOrders.forEach((o) => {
      const d = getOrderTimelineDate(o);
      if (!d) return;
      const key = d.toLocaleDateString();
      const prev = map.get(key) || { count: 0, total: 0 };
      map.set(key, {
        count: prev.count + 1,
        total: prev.total + parseNumber(o.deliveryTimeMinutes),
      });
    });
    return [...map.entries()].map(([date, v]) => ({
      date,
      avgDelivery: v.count ? v.total / v.count : 0,
    }));
  }, [filteredOrders]);

  // Cancellation breakdown
  const cancellationTable = useMemo(() => {
    const map = new Map();
    filteredOrders.forEach((o) => {
      if (!isCancelledStatus(o.__status)) return;
      const reason = (o.cancelReason || o.cancellationReason || "Unspecified").trim();
      const prev = map.get(reason) || 0;
      map.set(reason, prev + 1);
    });
    return [...map.entries()].map(([reason, count]) => ({
      id: reason,
      reason,
      count,
    }));
  }, [filteredOrders]);

  // Recent orders table
  const latestOrders = useMemo(() => {
    return [...filteredOrders]
      .sort((a, b) => {
        const da = getOrderTimelineDate(a)?.getTime() || 0;
        const db = getOrderTimelineDate(b)?.getTime() || 0;
        return db - da;
      })
      .slice(0, 25);
  }, [filteredOrders]);

  if (restaurantLoading) {
    return (
      <div className="reports-section">
        <p className="muted">Loading restaurant…</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="reports-section">
        <p className="muted">No restaurant found. Please sign in again.</p>
      </div>
    );
  }

  return (
    <div className="reports-section">
      <ReportFilterBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        grouping={null}
        onGroupingChange={null}
      />

      <section
        style={{
          marginBottom: 16,
          padding: 14,
          borderRadius: 14,
          background: "rgba(15, 23, 42, 0.04)",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        Operations on this page use restaurant-only sales values. Delivery fees and customer tips
        are excluded. Restaurant sales here mean food subtotal plus tax only.
      </section>

      <section className="reports-kpi-grid">
        <ReportCard title="Total Orders" value={kpis.total} />
        <ReportCard title="Completed" value={kpis.completed} />
        <ReportCard title="Cancelled" value={kpis.cancelled} />
        <ReportCard title="Refunded" value={kpis.refunded} />
        <ReportCard
          title="Restaurant Sales"
          value={formatCurrency(kpis.restaurantSalesTotal)}
          subtitle="Food subtotal + tax only"
        />
        <ReportCard
          title="Tax"
          value={formatCurrency(kpis.restaurantTax)}
          subtitle="Delivery fees and tips excluded"
        />
        <ReportCard
          title="Avg prep time"
          value={`${kpis.avgPrep.toFixed(1)} min`}
        />
        <ReportCard
          title="Avg delivery time"
          value={`${kpis.avgDelivery.toFixed(1)} min`}
        />
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Prep time by day</h3>
          <ReportChart
            type="line"
            data={prepTimeSeries}
            xKey="date"
            yKey="avgPrep"
          />
        </div>
        <div className="reports-column">
          <h3 className="reports-section-title">Delivery time by day</h3>
          <ReportChart
            type="line"
            data={deliveryTimeSeries}
            xKey="date"
            yKey="avgDelivery"
          />
        </div>
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Cancellation reasons</h3>
          <ReportTable
            columns={[
              { title: "Reason", dataIndex: "reason", key: "reason" },
              { title: "Count", dataIndex: "count", key: "count" },
            ]}
            data={cancellationTable}
          />
        </div>
        <div className="reports-column">
          <h3 className="reports-section-title">Latest orders</h3>
          <ReportTable
            columns={[
              { title: "ID", dataIndex: "id", key: "id" },
              {
                title: "Placed at",
                dataIndex: "createdAt",
                key: "createdAt",
                render: (v, row) => {
                  const d = getOrderTimelineDate(row);
                  if (!d) return "--";
                  return d.toLocaleString();
                },
              },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
              },
              {
                title: "Food subtotal",
                dataIndex: "__foodSubtotal",
                key: "__foodSubtotal",
                render: (v, row) =>
                  row.__salesEligible ? formatCurrency(v) : formatCurrency(0),
              },
              {
                title: "Tax",
                dataIndex: "__tax",
                key: "__tax",
                render: (v, row) =>
                  row.__salesEligible ? formatCurrency(v) : formatCurrency(0),
              },
              {
                title: "Restaurant total",
                dataIndex: "__restaurantSalesTotal",
                key: "__restaurantSalesTotal",
                render: (v) => formatCurrency(v),
              },
            ]}
            data={latestOrders}
          />
        </div>
      </section>

      {loading && <div className="reports-loading">Loading data…</div>}
    </div>
  );
};

export default OrdersReports;
